import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Modal from '../../molecules/Modal/Modal';
import Picto from '../../atoms/Picto/Picto';
import Button from '../../atoms/Button/Button';
import NavigationSearch from '../../molecules/NavigationSearch/NavigationSearch';
import MenuManagementTree from '../MenuManagementTree/MenuManagementTree';

// Domain
import { getMenuItemIdPath, getMenuItemIdsMatchingSearch } from '../../../domain/menu/menuManagementUseCase';
import { findMenuItemById } from '../../../domain/menu/menuNavigationUseCase';

// Style
import style from './MenuManagementModal.module.css';

/**
 * MenuManagementModal component - Edit menu visibility (checkbox) and favorites (star).
 * When `focusItemId` is set, its branch is expanded and scrolled into view.
 * @param {object} params
 * @param {boolean} params.isOpen - Whether the modal is open
 * @param {ApiManagerMenuItem[]} params.items - Draft menu tree
 * @param {string | null} [params.focusItemId=null] - Item opened from its popover
 * @param {(id: string, available: boolean) => void} params.onAvailableChange - Checkbox handler
 * @param {(id: string, favorite: boolean) => void} params.onFavoriteChange - Star handler
 * @param {() => void} params.onValidate - Validate handler
 * @param {() => void} params.onCancel - Cancel / close handler
 * @param {boolean} [params.isSaving=false] - Whether the save is pending
 * @returns {JSX.Element | null} MenuManagementModal component
 */
function MenuManagementModal({
  isOpen,
  items,
  focusItemId = null,
  onAvailableChange,
  onFavoriteChange,
  onValidate,
  onCancel,
  isSaving = false,
}) {
  const { t } = useTranslation();
  const contentRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [expandedIds, setExpandedIds] = useState(() => new Set(/** @type {string[]} */ ([])));
  // Expansion set by the opening: scroll to the focused item once it is rendered, not on later toggles
  const scrollPendingIdsRef = useRef(/** @type {Set<string> | null} */ (null));

  // Reset expansion on each opening (not on draft changes): the focused branch is unfolded
  useEffect(() => {
    if (!isOpen) return;
    const openingIds = new Set(getMenuItemIdPath(items, focusItemId));
    scrollPendingIdsRef.current = openingIds;
    setExpandedIds(openingIds);
  }, [isOpen, focusItemId]);

  useEffect(() => {
    if (!isOpen || !focusItemId || scrollPendingIdsRef.current !== expandedIds) return;
    scrollPendingIdsRef.current = null;
    contentRef.current
      ?.querySelector(`[data-menu-item-id="${CSS.escape(focusItemId)}"]`)
      ?.scrollIntoView({ block: 'start' });
  }, [isOpen, focusItemId, expandedIds]);

  const handleExpandToggle = (/** @type {string} */ id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [search, setSearch] = useState('');
  const matchingIds = useMemo(() => getMenuItemIdsMatchingSearch(items, search), [items, search]);

  // Each opening starts without search
  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const focusLabel = findMenuItemById(items, focusItemId)?.label;
  const title = focusLabel
    ? `${t('menu.management.title')} > ${focusLabel}`
    : t('menu.management.title');

  return (
    <Modal
      id="modal-menu-management"
      isOpen={isOpen}
      onClose={onCancel}
      className={style['menu-management-modal']}
      titleId="menu-management-title"
      header={(
        <div className={style.header}>
          <div className={style['header-title']}>
            <Picto icon="stickynote" className={style['header-icon']} />
            <p id="menu-management-title" className={style.title}>{title}</p>
          </div>
          <button
            type="button"
            className={style.close}
            onClick={onCancel}
            title={t('menu.management.cancel')}
            aria-label={t('menu.management.cancel')}
          >
            <Picto icon="times" />
          </button>
        </div>
      )}
    >
      <p className={style.description}>{t('menu.management.description')}</p>
      <div className={style.search}>
        <NavigationSearch
          id="menu-management-search"
          theme="form"
          placeholder={t('menu.search.placeholder')}
          isMenuOpen
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          onFocus={() => {}}
        />
      </div>
      <div ref={contentRef} className={style.content}>
        {matchingIds?.size === 0 ? <p className={style.empty}>{t('menu.search.empty')}</p> : null}
        <MenuManagementTree
          items={items}
          expandedIds={expandedIds}
          matchingIds={matchingIds}
          onExpandToggle={handleExpandToggle}
          onAvailableChange={onAvailableChange}
          onFavoriteChange={onFavoriteChange}
        />
      </div>
      <div className={style.footer}>
        <Button
          color="primary"
          size="small"
          className={style.validate}
          onClick={onValidate}
          isLoading={isSaving}
          disabled={isSaving}
        >
          {t('menu.management.validate')}
        </Button>
        <Button color="primary100" size="small" className={style.cancel} onClick={onCancel} disabled={isSaving}>
          {t('menu.management.cancel')}
        </Button>
      </div>
    </Modal>
  );
}

export default MenuManagementModal;
